import React from 'react';
import { 
  Cpu, 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Github,
  ArrowUp,
  Heart
} from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Approach', href: '#approach' },
    { name: 'Industries', href: '#industries' },
    { name: 'Contact', href: '#contact' },
  ];

  const services = [
    { name: 'vCTO & Strategy', href: '#services' },
    { name: 'Cybersecurity', href: '#services' },
    { name: 'Cloud Infrastructure', href: '#services' },
    { name: 'Web & Mobile Apps', href: '#services' },
    { name: 'CCTV & Security', href: '#services' },
    { name: 'IT Support', href: '#services' },
  ];

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Github, href: '#', label: 'GitHub' },
  ];

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-logo-navy text-white relative overflow-hidden mt-auto">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="tech-pattern"></div>
      </div>

      {/* Back to Top Button - Fixed positioning to not interfere with footer content */}
      <button
        onClick={scrollToTop}
        className="absolute -top-5 left-1/2 transform -translate-x-1/2
                   w-10 h-10 bg-gradient-to-r from-logo-darkblue to-logo-lightblue
                   rounded-full flex items-center justify-center shadow-lg
                   hover:scale-110 transition-all duration-300 animate-bounce
                   border-4 border-logo-navy z-20"
        aria-label="Back to top"
      >
        <ArrowUp className="text-white" size={20} />
      </button>

      <div className="container mx-auto px-4 sm:px-6 pt-16 pb-6 relative z-10">
        {/* Main Footer Content - Adjusted grid for better spacing */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-8 mb-8">
          {/* Brand Column - lg:col-span-4 */}
          <div className="lg:col-span-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-logo-green to-logo-yellow 
                            rounded-lg flex items-center justify-center flex-shrink-0">
                <Cpu className="text-logo-navy" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-bold">ADAPT</h1>
                <p className="text-xs text-logo-lightblue">TECHNOLOGY CONSULTING</p>
              </div>
            </div>
            
            <p className="text-blue-200 text-sm mb-4 leading-relaxed max-w-xs">
              Strategic IT Leadership for Growing Businesses. Your Virtual Chief Technology Officer (vCTO).
            </p>

            {/* Contact Info - Compact */}
            <div className="space-y-2">
              
              <div className="flex items-center space-x-2 text-blue-200 hover:text-white 
                            transition-colors group">
                <Phone size={14} className="group-hover:scale-110 transition-transform flex-shrink-0" />
                <span className="text-xs">( +27 ) 71 966 9546 / 631 08657</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-200 hover:text-white 
                            transition-colors group">
                <Mail size={14} className="group-hover:scale-110 transition-transform flex-shrink-0" />
                <span className="text-xs">info@adaptteck.co.za</span>
              </div>
            </div>
          </div>

          {/* Quick Links - lg:col-span-2 */}
          <div className="lg:col-span-2">
            <h3 className="text-base font-bold mb-3 text-white">Quick Links</h3>
            <ul className="space-y-1.5">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-blue-200 hover:text-white transition-all duration-300
                             hover:translate-x-1 inline-flex items-center text-xs"
                  >
                    <span className="w-1 h-1 bg-logo-green rounded-full mr-2"></span>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services - lg:col-span-3 */}
          <div className="lg:col-span-3">
            <h3 className="text-base font-bold mb-3 text-white">Our Services</h3>
            <ul className="grid grid-cols-2 gap-1.5">
              {services.map((service) => (
                <li key={service.name}>
                  <a
                    href={service.href}
                    className="text-blue-200 hover:text-white transition-all duration-300
                             hover:translate-x-1 inline-flex items-center text-xs"
                  >
                    <span className="w-1 h-1 bg-logo-green rounded-full mr-2"></span>
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter & Social - lg:col-span-3 */}
          <div className="lg:col-span-3">
            <h3 className="text-base font-bold mb-3 text-white">Stay Updated</h3>
            <p className="text-blue-200 text-xs mb-3">
              Subscribe for tech insights and updates.
            </p>
            
            {/* Compact Newsletter Form */}
            <form className="flex mb-4">
              <input
                type="email"
                placeholder="Your email"
                className="flex-1 px-3 py-2 text-xs bg-white/10 border border-white/20 
                         rounded-l-lg focus:outline-none focus:ring-1 focus:ring-logo-lightblue
                         text-white placeholder-blue-200/50"
              />
              <button
                type="submit"
                className="px-3 py-2 text-xs bg-gradient-to-r from-logo-green to-logo-yellow
                         text-logo-navy font-medium rounded-r-lg hover:opacity-90
                         transition-all duration-300 whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>

            {/* Social Links - Compact */}
            <div className="flex space-x-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center
                             hover:bg-gradient-to-r hover:from-logo-darkblue hover:to-logo-lightblue
                             transition-all duration-300 hover:scale-110 group"
                    aria-label={social.label}
                  >
                    <Icon size={14} className="text-white group-hover:scale-110 transition-transform" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar - Reduced top padding and margin */}
        <div className="pt-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center text-xs">
            <p className="text-blue-200 mb-3 md:mb-0">
              © {currentYear} ADAPT Technology Solutions. All rights reserved.
            </p>
            <p className="text-blue-200 flex items-center mb-3 md:mb-0">
              Made with <Heart className="text-red-400 mx-1" size={12} /> for innovation
            </p>
            <p className="text-blue-200">
              Promoting digital inclusion
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;